import { fork, takeLatest, take, put, select } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { browserHistory } from 'react-router';
import { CHATS_FILTER_TYPES } from '../../../constants/chat';
import { PROGRAM_VALUE_TYPES } from '../../../constants/props';
import {
  connectionReadySelector,
  availableTargetUserChannelsDataSelector,
  socialChatSelector,
} from '../../chatConnection/selectors';
import {
  loggedUserSelector,
  targetUserIdSelector,
} from '../../context/selectors';
import { subChannelsSelector } from '../selectors/channels';
import {
  availableProgramsSelector,
  programValueSelector,
  searchQuerySelector,
  statusSelector,
  subFiltersSelector,
  typeSelector,
} from '../selectors/filters';
import {
  limitSelector,
  offsetSelector,
  excludedIdsSelector,
} from '../selectors/settings';

import { consts as reqChannels } from '../reqChannels';
import { consts as reqNewChannel } from '../reqNewChannel';
import { consts as reqSocialChannel } from '../reqSocialChannel';
import { consts as reqFilters } from '../reqFilters';
import { consts as channels } from '../channels';
import { consts as chatConsts } from '../../chatConnection';
import { consts as filters } from '../filters';
import { consts as loggedUserConsts } from '../../context/loggedUser';
import { consts as settings } from '../settings';
import { consts as context } from '../../context';
import { MESSAGES } from '../../../constants/routes';
import { currentLocationSelector } from '../../common/selectors';

function* getItemsWorker(action) {
  // await for system channel connection
  const connectionReady = yield select(connectionReadySelector);

  if (!connectionReady) {
    yield take(chatConsts.CONNECTION_READY);
  }

  // await for logged user storing
  const loggedUser = yield select(loggedUserSelector);

  if (isEmpty(loggedUser)) {
    yield take(loggedUserConsts.ADD);
  }

  // clear channels and settings
  if (action.type !== settings.LOAD_NEXT_PAGE) {
    yield put({ type: channels.CLEAR });
    yield put({
      type: settings.SET_OFFSET,
      payload: 0,
    });
    yield put({ type: settings.CLEAR_EXCLUDED_IDS });
  }

  // for first render - request available filters
  if (action.type === channels.GET_ITEMS) {
    // request
    yield put({ type: reqFilters.SEND });
    // await for response
    yield take(filters.SET_AVAILABLE_FILTERS);
  }

  // request available channels
  const state = yield select();
  const hasSubFilters = subFiltersSelector(state);

  let params = {
    type: typeSelector(state),
    limit: limitSelector(state),
    offset: offsetSelector(state),
    query: searchQuerySelector(state),
    excludedIds: excludedIdsSelector(state),
  };

  if (hasSubFilters) {
    // check correctness of restored filters
    const availableProgramValues = availableProgramsSelector(state).map(
      program => program.filterValue,
    );
    const retrievedProgramValue = programValueSelector(state);

    const program = availableProgramValues.includes(retrievedProgramValue)
      ? retrievedProgramValue
      : PROGRAM_VALUE_TYPES.all;

    params = {
      ...params,
      program,
      statuses: statusSelector(state),
    };
  }

  yield put({
    type: reqChannels.SEND,
    payload: params,
  });

  while (true) {
    yield take(channels.ADD_ITEMS);
    const availableSubChannels = yield select(subChannelsSelector);

    const actionType =
      action.type === settings.LOAD_NEXT_PAGE
        ? 'UPDATE_AVAILABLE_CHANNELS'
        : 'SET_AVAILABLE_CHANNELS';

    yield put({
      type: chatConsts[actionType],
      payload: availableSubChannels,
    });
  }
}

export function* getItemsSaga() {
  yield takeLatest(
    [
      channels.GET_ITEMS,
      filters.SET_TYPE,
      filters.SET_PROGRAM_VALUE,
      filters.SET_SEARCH_QUERY,
      filters.SET_STATUS,
      settings.LOAD_NEXT_PAGE,
      settings.UPDATE_LAST_USER_ID,
    ],
    getItemsWorker,
  );
}

function* addItemsWorker(action) {
  yield put({
    type: channels.ADD_ITEMS,
    payload: action.payload,
  });
}

export function* addItemsSaga() {
  yield takeLatest(reqChannels.SUCCEED, addItemsWorker);
}

export function* addNewChannelWorker(action) {
  const target = yield select(targetUserIdSelector);
  const userChannels = yield select(availableTargetUserChannelsDataSelector);
  const { channelId } = action.payload;

  if (!find(userChannels, { channelId })) {
    yield put({
      type: chatConsts.ADD_NEW_SUBCHANNEL,
      payload: { subChannel: action.payload, id: target },
    });
    yield put({
      type: chatConsts.UPDATE_CHANNEL_SUBCHANNELS,
      payload: { subChannel: action.payload, id: target },
    });
  }
  yield put({
    type: chatConsts.SET_ACTIVE_CHANNEL_ID,
    payload: channelId,
  });
}

export function* addNewChannelSaga() {
  yield takeLatest(reqNewChannel.SUCCEED, addNewChannelWorker);
}

export function* addSocialChatWorker(action) {
  const { channelId } = action.payload.contextChannel;
  const socialChannels = yield select(socialChatSelector);
  const currentLocation = yield select(currentLocationSelector);

  yield put({
    type: filters.SET_TYPE,
    payload: CHATS_FILTER_TYPES.ALL,
  });

  if (!find(socialChannels, { channelId })) {
    yield put({
      type: chatConsts.ADD_SOCIAL_CHAT,
      payload: action.payload,
    });
  }

  yield put({
    type: context.CLEAR,
  });

  if (currentLocation !== `/${MESSAGES}`) {
    yield browserHistory.push(`/${MESSAGES}`);
  }

  yield put({
    type: chatConsts.SET_ACTIVE_CHANNEL_ID,
    payload: channelId,
  });

  yield put({
    type: settings.SET_LAST_SOCIAL_ID,
    payload: channelId,
  });
}

export function* addSocialChatSaga() {
  yield takeLatest(reqSocialChannel.SUCCEED, addSocialChatWorker);
}

export default function* root() {
  yield fork(getItemsSaga);
  yield fork(addItemsSaga);
  yield fork(addNewChannelSaga);
  yield fork(addSocialChatSaga);
}



// WEBPACK FOOTER //
// ./src/redux/board/sagas/channels.js